import React, { useState, useEffect } from 'react';

const AdminDonations = () => {
  const [donations, setDonations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState({ isOpen: false, type: 'alert', title: '', message: '', onConfirm: null });

  const showAlert = (title, message, onConfirm = null) => {
    setDialog({ isOpen: true, type: 'alert', title, message, onConfirm });
  };

  const showConfirm = (title, message, onConfirm) => {
    setDialog({ isOpen: true, type: 'confirm', title, message, onConfirm });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    fetchDonations();
  }, []);

  const fetchDonations = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/donations`);
      if (!response.ok) throw new Error('Failed to fetch donations');
      const data = await response.json();
      setDonations(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = (id) => {
    showConfirm(
      'Delete Donation Record',
      'Are you sure you want to delete this donation record? This action cannot be undone.',
      () => executeDelete(id)
    );
  };

  const executeDelete = async (id) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/donations/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete donation');
      
      showAlert('Success', 'Donation record has been deleted!');
      setDonations(donations.filter(d => d.id !== id));
    } catch (err) {
      showAlert('Error', err.message || 'Failed to delete donation. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('hr-HR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-electric-yellow"></div>
      </div>
    );
  }

  const successCount = donations.filter(d => d.status === 'success').length;
  const totalRevenue = donations
    .filter(d => d.status === 'success')
    .reduce((sum, d) => sum + Number(d.amount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="text-left">
          <h2 className="font-clash text-2xl md:text-3xl font-bold text-electric-yellow">Donations Inbox</h2>
          <p className="text-soft-gray mt-1 text-sm md:text-base">Review your supporter transaction histories in real time.</p>
        </div>
        <div className="flex w-full sm:w-auto justify-between sm:justify-end gap-6 text-left sm:text-right mt-2 sm:mt-0">
          <div>
            <div className="text-sm text-soft-gray">Total Contributors</div>
            <div className="text-xl md:text-2xl font-bold text-warm-white">{successCount}</div>
          </div>
          <div>
            <div className="text-sm text-soft-gray">Gross Received</div>
            <div className="text-xl md:text-2xl font-bold text-electric-yellow">€{totalRevenue.toFixed(2)}</div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-4 rounded-xl">
          {error}
        </div>
      )}

      <div className="bg-charcoal rounded-xl border border-slate-dark/30 overflow-hidden shadow-2xl">
        <div className="overflow-x-auto w-full">
          <table className="w-full text-left min-w-[600px]">
            <thead className="bg-[#1A1A1A] border-b border-slate-dark/50">
              <tr>
                <th className="px-6 py-4 text-sm font-semibold text-soft-gray whitespace-nowrap">Supporter</th>
                <th className="px-6 py-4 text-sm font-semibold text-soft-gray whitespace-nowrap">Amount</th>
                <th className="px-6 py-4 text-sm font-semibold text-soft-gray whitespace-nowrap">Status</th>
                <th className="px-6 py-4 text-sm font-semibold text-soft-gray whitespace-nowrap">Timestamp</th>
                <th className="px-6 py-4 text-sm font-semibold text-soft-gray text-right whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-dark/30">
              {donations.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-soft-gray">
                    No donations tracked internally yet.
                  </td>
                </tr>
              ) : (
                donations.map((donation) => (
                  <tr key={donation.id} className="hover:bg-slate-dark/10 transition-colors">
                    <td className="px-6 py-4 text-warm-white whitespace-nowrap">
                      <div className="font-medium">
                        {donation.isAnonymous ? (
                          <span className="text-electric-yellow/80 italic">Anonymous ({donation.name})</span>
                        ) : (
                          donation.name
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-warm-white font-bold whitespace-nowrap">€{Number(donation.amount).toFixed(2)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        donation.status === 'success' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {donation.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-soft-gray text-sm whitespace-nowrap">
                      {formatDate(donation.createdAt)}
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      <button
                        onClick={() => handleDelete(donation.id)}
                        className="text-red-400 hover:text-red-300 transition-colors"
                        title="Delete Sequence Record"
                      >
                        <svg className="w-5 h-5 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Custom Dialog Alert/Confirm Modal */}
      {dialog.isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-midnight/80 backdrop-blur-sm">
          <div className="bg-slate-dark border border-electric-yellow/30 rounded-2xl p-6 max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-200">
            <h3 className="text-xl font-bold text-electric-yellow mb-2">{dialog.title}</h3>
            <p className="text-soft-gray mb-6">{dialog.message}</p>
            <div className="flex justify-center gap-3">
              {dialog.type === 'confirm' && (
                <button
                  onClick={closeDialog}
                  className="px-4 py-2 rounded-lg font-medium text-soft-gray hover:text-white hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={() => {
                  if (dialog.onConfirm) dialog.onConfirm();
                  closeDialog();
                }}
                className={`px-4 py-2 rounded-lg font-bold transition-transform hover:scale-105 ${
                  dialog.type === 'confirm' 
                    ? 'bg-red-500 text-white shadow-lg shadow-red-500/20' 
                    : 'bg-electric-yellow text-midnight shadow-lg shadow-electric-yellow/20'
                }`}
              >
                {dialog.type === 'confirm' ? 'Confirm' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDonations;
